import { X, Check, Mail, FileText, User, Calendar, Phone, Users, AlignLeft, ShieldCheck } from 'lucide-react';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addDoctor, checkLicenseExists } from '../../redux/doctorSlice';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import { useTranslation } from 'react-i18next';
import Swal from "sweetalert2";
import { toast } from 'react-toastify';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import GenderSelect from '../common/GenderSelect';
import { docPatCreationCountryList, getAllProfessionsList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import ProfessionSelect from '../common/ProfessionSelect';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';

export default function AddDoctor({ isOpen, onClose, onSubmit }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation(['addDoctors', 'common']);
    const { licenseCheckLoading } = useSelector((state) => state.doctor);
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth);
    const { countryTimezoneList, professionListAll, professionListAllLoading } = useSelector((state) => state.master);
    const axiosInstance = useAxiosAuth();
    const isInitialCall = useRef(true);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        gender: '',
        dob: '',
        email: '',
        country: '',
        countryId: '',
        timeZone: '',
        phone: '',
        altPhone: '',
        licenseNumber: '',
        description: '',
        profession: [],
    });
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;

    useEffect(() => {
        dispatch(docPatCreationCountryList());
        dispatch(getAllProfessionsList());
    }, [dispatch]);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [charLimitReached, setCharLimitReached] = useState({});
    const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef(''); // Track last checked email
    const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress
    const [licenseStatus, setLicenseStatus] = useState(null); // null: not checked, true: available, false: exists
    const licenseCheckTimeoutRef = useRef(null);
    const lastCheckedLicenseRef = useRef(''); // Track last checked license number
    const isLicenseCheckInProgressRef = useRef(false); // Track if license check is in progress

    // --- Email OTP state ---
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    // --- Phone OTP state ---
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // Store dispatch and axiosInstance in refs to avoid dependency issues
    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    // Update refs when values change
    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the email hasn't changed during the API call
                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email already exists (not available)
                        // exists: false means email doesn't exist (available)
                        setEmailStatus(!resultAction.payload.exists);
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = ''; // Reset on error
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email, isOpen]);

    // Debounced phone check (only for main phone, not altPhone)
    useEffect(() => {
        if (!isOpen) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';

        // Need a valid phone number before checking
        const isValidIndia = formData.country === 'India' && /^\d{10}$/.test(phone);
        const isValidFrance = formData.country === 'France' && /^\d{9}$/.test(phone);

        if (!phone || (!isValidIndia && !isValidFrance)) {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();

            const isStillValidIndia = formData.country === 'India' && /^\d{10}$/.test(trimmedPhone);
            const isStillValidFrance = formData.country === 'France' && /^\d{9}$/.test(trimmedPhone);

            if (!trimmedPhone || (!isStillValidIndia && !isStillValidFrance) || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);

        return () => {
            if (phoneCheckTimeoutRef.current) {
                clearTimeout(phoneCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.phone, formData.country, isOpen]);

    // Debounced license check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (licenseCheckTimeoutRef.current) {
            clearTimeout(licenseCheckTimeoutRef.current);
        }

        const licenseNumber = formData.licenseNumber?.trim() || '';

        // If license number is empty, reset status
        if (!licenseNumber) {
            setLicenseStatus(null);
            lastCheckedLicenseRef.current = '';
            return;
        }

        // If license number hasn't changed or check is in progress, don't check again
        if (lastCheckedLicenseRef.current === licenseNumber || isLicenseCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        licenseCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedLicense = formData.licenseNumber.trim();

            // Double check license is still valid and hasn't changed during debounce
            if (!trimmedLicense || lastCheckedLicenseRef.current === trimmedLicense || isLicenseCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked license BEFORE making the API call
            isLicenseCheckInProgressRef.current = true;
            lastCheckedLicenseRef.current = trimmedLicense;

            try {
                const resultAction = await dispatchRef.current(checkLicenseExists({
                    licenseNumber: trimmedLicense,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the license hasn't changed during the API call
                if (formData.licenseNumber.trim() === trimmedLicense) {
                    if (checkLicenseExists.fulfilled.match(resultAction)) {
                        // exists: true means license already exists (not available)
                        // exists: false means license doesn't exist (available)
                        setLicenseStatus(!resultAction.payload.exists);
                    } else {
                        setLicenseStatus(null);
                        lastCheckedLicenseRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking license:', error);
                if (formData.licenseNumber.trim() === trimmedLicense) {
                    setLicenseStatus(null);
                    lastCheckedLicenseRef.current = ''; // Reset on error
                }
            } finally {
                isLicenseCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (licenseCheckTimeoutRef.current) {
                clearTimeout(licenseCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.licenseNumber, isOpen]);

    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addDoctors.failed_to_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('addDoctors.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addDoctors.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addDoctors.failed_to_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('addDoctors.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addDoctors.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    // Cleanup OTP timers on unmount
    useEffect(() => {
        return () => {
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        };
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            surname: '',
            gender: '',
            dob: '',
            email: '',
            country: '',
            countryId: '',
            timeZone: '',
            phone: '',
            altPhone: '',
            licenseNumber: '',
            description: '',
            profession: [],
        });
        setErrors({});
        setApiError(null);
        setCharLimitReached({});
        setEmailStatus(null);
        setPhoneStatus(null);
        setLicenseStatus(null);
        lastCheckedEmailRef.current = '';
        lastCheckedPhoneRef.current = '';
        lastCheckedLicenseRef.current = '';
        isEmailCheckInProgressRef.current = false;
        isPhoneCheckInProgressRef.current = false;
        isLicenseCheckInProgressRef.current = false;
        resetOtpEmailState();
        resetOtpPhoneState();
    };

    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t('addDoctors.nameisrequired');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('addDoctors.name_only_spaces');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.name.trim())) {
            newErrors.name = t('addDoctors.name_invalid_chars');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('addDoctors.name_min_length');
        }

        if (!formData.surname.trim()) {
            newErrors.surname = t('addDoctors.surnameisrequired');
        } else if (/^\s+$/.test(formData.surname)) {
            newErrors.surname = t('addDoctors.surname_only_spaces');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.surname.trim())) {
            newErrors.surname = t('addDoctors.surname_invalid_chars');
        } else if (formData.surname.trim().length < 2) {
            newErrors.surname = t('addDoctors.surname_min_length');
        }

        if (!formData.gender) newErrors.gender = t('addDoctors.genderisrequired');
        if (!formData.dob) {
            newErrors.dob = t('addDoctors.dobisrequired');
        } else if (new Date(formData.dob) > new Date()) {
            newErrors.dob = t('addDoctors.futuredob');
        } else if (calculateAge(formData.dob) < 18) {
            newErrors.dob = t('addDoctors.18plus');
        } else if (new Date(formData.dob).getFullYear() < 1900) {
            newErrors.dob = t('addDoctors.dob_must_be_after_1900');
        }
        if (!formData.email.trim()) {
            newErrors.email = t('addDoctors.emailisrequired');
        } else {
            const email = formData.email.trim();
            // Check total length (RFC 5321: max 254 characters)
            if (email.length > 254) {
                newErrors.email = t('addDoctors.email_too_long');
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('addDoctors.invalidemailformat');
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                    } else if (localPart.length === 0) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (emailStatus === false) {
                        newErrors.email = t('addDoctors.email_already_exists');
                    } else if (emailStatus === null && !emailCheckLoading) {
                        newErrors.email = t('addDoctors.verify_email_address');
                    } else if (emailStatus === true && !emailOtpVerified) {
                        newErrors.email = t('addDoctors.verify_email_otp');
                    }
                }
            }
        }
        if (!formData.countryId) newErrors.countryId = t('addDoctors.countryisrequired');
        if (!formData.phone.trim()) newErrors.phone = t('addDoctors.phonenumberisrequired');
        else {
            const phoneMin = selectedCountry?.mobileMinLength || 10;
            const phoneMax = selectedCountry?.mobileMaxLength || 10;
            const phoneRegex = new RegExp(`^\\+?\\d{${phoneMin},${phoneMax}}$`);
            if (!phoneRegex.test(formData.phone)) {
                newErrors.phone = t('addDoctors.invalid_phone_number', { defaultValue: `Phone number must be between ${phoneMin} and ${phoneMax} digits.`, length: phoneMin === phoneMax ? phoneMin : `${phoneMin}-${phoneMax}` });
            } else if (phoneStatus === false) {
                newErrors.phone = t('addDoctors.phone_already_exists');
            } else if (phoneStatus === null && !mobileCheckLoading) {
                newErrors.phone = t('addDoctors.verify_phone_number');
            } else if (phoneStatus === true && !phoneOtpVerified) {
                newErrors.phone = t('addDoctors.verify_phone_otp');
            }
        }

        if (formData.altPhone && formData.altPhone.trim()) {
            const altMin = selectedCountry?.altMinLength || selectedCountry?.mobileMinLength || 10;
            const altMax = selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 10;
            const altPhoneRegex = new RegExp(`^\\+?\\d{${altMin},${altMax}}$`);
            if (!altPhoneRegex.test(formData.altPhone)) {
                newErrors.altPhone = t('addDoctors.invalid_alt_phone_number', { defaultValue: `Alt phone number must be between ${altMin} and ${altMax} digits.`, length: altMin === altMax ? altMin : `${altMin}-${altMax}` });
            }
        }

        if (!formData.profession || formData.profession.length === 0) {
            newErrors.profession = t('addDoctors.profession_required', 'Please select at least 1 profession');
        } else if (formData.profession.length > 3) {
            newErrors.profession = t('addDoctors.profession_max_exceeded', 'Maximum 3 professions allowed');
        }

        if (formData.licenseNumber.trim()) {
            if (formData.licenseNumber.trim().length < 3) {
                newErrors.licenseNumber = t('addDoctors.license_min_length');
            } else if (formData.licenseNumber.trim().length > 20) {
                newErrors.licenseNumber = t('addDoctors.license_max_length');
            } else if (!/^[a-zA-Z0-9]+$/.test(formData.licenseNumber.trim())) {
                newErrors.licenseNumber = t('addDoctors.license_invalid_chars');
            } else if (licenseStatus === false) {
                newErrors.licenseNumber = t('addDoctors.license_already_exists');
            } else if (licenseStatus === null && !licenseCheckLoading) {
                newErrors.licenseNumber = t('addDoctors.verify_license_number');
            }
        }

        // Description validation (optional field, but if provided, validate)
        if (formData.description && formData.description.trim()) {
            // Sanitize description: remove potentially dangerous characters
            const sanitized = formData.description.replace(/[<>]/g, '');
            if (sanitized !== formData.description) {
                // Update formData with sanitized value
                setFormData(prev => ({ ...prev, description: sanitized }));
            }
            if (formData.description.length > 500) {
                newErrors.description = t('addDoctors.description_max_length');
            } else if (/^\s+$/.test(formData.description)) {
                newErrors.description = t('addDoctors.description_only_spaces');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = {
            name: 20,
            surname: 20,
            phone: selectedCountry?.mobileMaxLength || 15,
            altPhone: selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 15,
            licenseNumber: 20
        };
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): always uppercase.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Restrict phone and altPhone to digits only
        if ((name === "phone" || name === "altPhone") && processedValue && !/^\d*$/.test(processedValue)) return;

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === "name" || name === "surname") && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) return;

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === "email" && value) {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (!/^[a-zA-Z0-9._+-@]*$/.test(value)) return;
        }

        // Restrict licenseNumber to alphanumeric only (no special characters)
        if (name === "licenseNumber" && value && !/^[a-zA-Z0-9]*$/.test(value)) return;

        // Sanitize description: remove potentially dangerous characters
        if (name === "description" && value) {
            const sanitized = value.replace(/[<>]/g, '');
            if (sanitized !== value) {
                // Update with sanitized value
                setFormData((prev) => ({
                    ...prev,
                    [name]: sanitized,
                }));
                setApiError(null);
                return;
            }
        }

        // Check if character limit is reached
        if (maxLengths[name] && value.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
            resetOtpEmailState();
        }

        // Reset phone status and tracking when phone changes
        if (name === 'phone') {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            isPhoneCheckInProgressRef.current = false;
            resetOtpPhoneState();
        }

        // Reset license status and tracking when license number changes
        if (name === 'licenseNumber') {
            setLicenseStatus(null);
            lastCheckedLicenseRef.current = '';
            isLicenseCheckInProgressRef.current = false;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
            ...(name === 'dob' && processedValue ? { age: calculateAge(processedValue) } : {}),
        }));
        setApiError(null);

        // Clear error for this field when user starts typing, or apply live validation for dob
        if (name === 'dob') {
            let dobError = '';
            if (!processedValue) {
                dobError = t('addDoctors.dobisrequired');
            } else if (new Date(processedValue) > new Date()) {
                dobError = t('addDoctors.futuredob');
            } else if (calculateAge(processedValue) < 18) {
                dobError = t('addDoctors.18plus');
            } else if (new Date(processedValue).getFullYear() < 1900) {
                dobError = t('addDoctors.dob_must_be_after_1900');
            }

            setErrors((prev) => ({ ...prev, dob: dobError }));
        } else if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // Ensure email is validated and OTP verified before submission
        if (emailStatus !== true) {
            toast.warn(t('addDoctors.verify_email_to_proceed', 'Please verify your email address. It must be available to proceed.'));
            return;
        }
        if (!emailOtpVerified) {
            toast.warn(t('addDoctors.verify_email_otp'));
            return;
        }

        // Ensure phone is validated and OTP verified before submission
        if (phoneStatus !== true) {
            toast.warn(t('addDoctors.verify_phone_to_proceed'));
            return;
        }
        if (!phoneOtpVerified) {
            toast.warn(t('addDoctors.verify_phone_otp'));
            return;
        }

        // Ensure license is validated before submission if provided
        if (formData.licenseNumber.trim() && licenseStatus !== true) {
            toast.warn(t('addDoctors.verify_license_to_proceed', 'Please verify your license number. It must be available to proceed.'));
            return;
        }

        const result = await Swal.fire({
            title: t('addDoctors.confirmTitle'),
            text: t('addDoctors.confirmText'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('addDoctors.confirmButtonText'),
            cancelButtonText: t('addDoctors.cancelButtonText'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);
        setApiError(null);

        try {
            const doctorRes = await dispatch(
                addDoctor({
                    formData: {
                        name: formData.name,
                        surname: formData.surname,
                        gender: formData.gender,
                        dob: formData.dob,
                        email: formData.email,
                        phone: formData.phone,
                        altPhone: formData.altPhone,
                        licenseNumber: formData.licenseNumber,
                        description: formData.description,
                        countryId: formData.countryId,
                        timeZone: formData.timeZone,
                        profession: formData.profession.length > 0 ? formData.profession : undefined,
                        profileStatus: 'complete',
                        ...(emailOtpVerified && { emailVerified: true }),
                        ...(phoneOtpVerified && { phoneNoVerified: true }),
                    }
                })
            ).unwrap();

            if (!doctorRes || !doctorRes.doctor || !doctorRes.doctor.doctorId || doctorRes.success === false) {
                throw new Error(
                    doctorRes?.error?.message?.en ||
                    doctorRes?.error?.message ||
                    t('addDoctors.doccreationfailed')
                );
            }

            toast.success(t('addDoctors.successText'));

            onSubmit({
                success: true,
                doctor: doctorRes,
            });

            resetForm();
            onClose();

        } catch (error) {
            console.log('error', error);
            const errMsg =
                error.error?.response?.data?.message?.en ||
                error.message ||
                t('addDoctors.erroroccured');

            setApiError(errMsg);

            toast.warn(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div data-testid="add-doctor-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div data-testid="add-doctor-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                {/* Header Section - Sticky */}
                <div data-testid="add-doctor-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                    <h3 data-testid="add-doctor-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('addDoctors.heading')}</h3>
                    <button
                        data-testid="close-add-doctor-modal"
                        onClick={handleClose}
                        className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                    >
                        <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                    {apiError && (
                        <div data-testid="add-doctor-api-error" className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl mb-4 shadow-md">
                            {apiError}
                        </div>
                    )}
                    <form data-testid="add-doctor-form" onSubmit={handleSubmit} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
                        {/* 1. Gender */}
                        <div data-testid="gender-field-container">
                            <label htmlFor="gender" data-testid="gender-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.gender')} <span className="text-red-500">*</span></label>
                            <GenderSelect
                                value={formData.gender}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, gender: val }));
                                    setErrors(prev => ({ ...prev, gender: null }));
                                }}
                                options={getGenderOptions(t)}
                                placeholder={t('addDoctors.selectgender')}
                                error={!!errors.gender}
                            />
                            {errors.gender && <p data-testid="gender-error" className="mt-2 text-xs text-red-500 font-medium">{errors.gender}</p>}
                        </div>
                        {/* 2. First Name */}
                        <div data-testid="name-field-container">
                            <label htmlFor="name" data-testid="name-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.name')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="name-input"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.name_placeholder')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.name && <p data-testid="name-error" className="mt-2 text-xs text-red-500 font-medium">{errors.name}</p>}
                            {charLimitReached.name && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        {/* 3. Surname */}
                        <div data-testid="surname-field-container">
                            <label htmlFor="surname" data-testid="surname-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.surname')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="surname-input"
                                    name="surname"
                                    type="text"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.surname_placeholder')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm uppercase"
                                />
                            </div>
                            {errors.surname && <p data-testid="surname-error" className="mt-2 text-xs text-red-500 font-medium">{errors.surname}</p>}
                            {charLimitReached.surname && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        <div data-testid="dob-field-container">
                            <label htmlFor="dob" data-testid="dob-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.dob')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="dob-input"
                                    name="dob"
                                    type="date"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    min="1900-01-01"
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                                />
                            </div>
                            {errors.dob && <p data-testid="dob-error" className="mt-2 text-xs text-red-500 font-medium">{errors.dob}</p>}
                        </div>
                        <div data-testid="age-field-container">
                            <label htmlFor="age" data-testid="age-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.age')}</label>
                            <div className="relative flex items-center border rounded-xl p-3 bg-gray-100 border-gray-300">
                                <Calendar className="text-gray-400 w-5 h-5 mr-2" />
                                <input
                                    data-testid="age-input"
                                    name="age"
                                    type="number"
                                    value={formData.dob && !errors.dob ? calculateAge(formData.dob) : ''}
                                    readOnly
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div data-testid="email-field-container">
                            <label htmlFor="email" data-testid="email-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.email')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${emailOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : emailStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : emailStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.email
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                <input
                                    data-testid="email-input"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.email_placeholder')}
                                    maxLength={254}
                                    disabled={emailOtpVerified}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                                {emailCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!emailCheckLoading && emailStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, email: '' }));
                                            setEmailStatus(null);
                                            setErrors(prev => ({ ...prev, email: '' }));
                                            lastCheckedEmailRef.current = '';
                                            resetOtpEmailState();
                                        }}
                                    />
                                )}
                                {!emailCheckLoading && emailStatus === true && !emailOtpVerified && (
                                    <button
                                        type="button"
                                        onClick={handleSendEmailOtp}
                                        disabled={sendOtpLoading || emailOtpSent}
                                        className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        data-testid="add-doctor-send-email-otp-button"
                                    >
                                        {sendOtpLoading ? '...' : emailOtpSent ? t('addDoctors.sent') : t('addDoctors.send_otp')}
                                    </button>
                                )}
                                {emailOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.email && <p data-testid="email-error" className="mt-2 text-xs text-red-500 font-medium">{errors.email}</p>}
                            {!emailCheckLoading && emailStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addDoctors.email_already_exists')}</p>
                            )}
                            {/* Email OTP input row */}
                            {emailOtpSent && !emailOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            data-testid="email-otp-input"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={emailOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setEmailOtpCode(val);
                                                setEmailOtpError('');
                                                if (val.length === 6) handleVerifyEmailOtp(val);
                                            }}
                                            placeholder={t('addDoctors.enter_otp')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            data-testid="email-otp-verify-button"
                                            type="button"
                                            onClick={() => handleVerifyEmailOtp()}
                                            disabled={verifyOtpLoading || emailOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addDoctors.verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {emailOtpTimer > 0 ? (
                                            <span data-testid="email-otp-timer" className="text-xs text-gray-500">{t('addDoctors.resend_in', { seconds: emailOtpTimer })}</span>
                                        ) : (
                                            <button
                                                data-testid="email-otp-resend-button"
                                                type="button"
                                                onClick={handleSendEmailOtp}
                                                disabled={sendOtpLoading}
                                                className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50"
                                            >
                                                {t('addDoctors.resend_otp')}
                                            </button>
                                        )}
                                    </div>
                                    {emailOtpError && <p data-testid="email-otp-error" className="text-red-500 text-xs">{emailOtpError}</p>}
                                </div>
                            )}
                            {emailOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addDoctors.email_otp_verified')}</p>
                            )}
                        </div>
                        <div data-testid="phone-field-container">
                            <label
                                htmlFor="phone"
                                data-testid="phone-label"
                                className="block text-sm font-semibold text-gray-700 mb-2"
                            >
                                {t('addDoctors.phone')} <span className="text-red-500">*</span>
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'} ${phoneOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : phoneStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : phoneStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.phone
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 flex-shrink-0 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <PhoneCountryDropdown
                                    countryTimezoneList={countryTimezoneList}
                                    countryId={formData.countryId}
                                    onSelect={({ countryId, timeZone, countryName }) => {
                                        setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                        setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                        setPhoneStatus(null);
                                        lastCheckedPhoneRef.current = '';
                                    }}
                                    disabled={phoneOtpVerified}
                                />
                                <input
                                    data-testid="phone-input"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    placeholder={!formData.countryId ? t('addDoctors.select_country_first', 'Select country first') : t('addDoctors.phone_placeholder')}
                                    maxLength={selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId || phoneOtpVerified}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                                {mobileCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!mobileCheckLoading && phoneStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, phone: '' }));
                                            setPhoneStatus(null);
                                            setErrors(prev => ({ ...prev, phone: '' }));
                                            lastCheckedPhoneRef.current = '';
                                            resetOtpPhoneState();
                                        }}
                                    />
                                )}
                                {!mobileCheckLoading && phoneStatus === true && !phoneOtpVerified && (
                                    <button
                                        type="button"
                                        onClick={handleSendPhoneOtp}
                                        disabled={sendOtpLoading || phoneOtpSent}
                                        className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        data-testid="add-doctor-send-phone-otp-button"
                                    >
                                        {sendOtpLoading ? '...' : phoneOtpSent ? t('addDoctors.sent') : t('addDoctors.send_otp')}
                                    </button>
                                )}
                                {phoneOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.phone && (
                                <p data-testid="phone-error" className="mt-2 text-xs text-red-500 font-medium">
                                    {errors.phone}
                                </p>
                            )}
                            {!mobileCheckLoading && phoneStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addDoctors.phone_already_exists')}</p>
                            )}
                            {/* Phone OTP input row */}
                            {phoneOtpSent && !phoneOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            data-testid="phone-otp-input"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={phoneOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setPhoneOtpCode(val);
                                                setPhoneOtpError('');
                                                if (val.length === 6) handleVerifyPhoneOtp(val);
                                            }}
                                            placeholder={t('addDoctors.enter_otp')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            data-testid="phone-otp-verify-button"
                                            type="button"
                                            onClick={() => handleVerifyPhoneOtp()}
                                            disabled={verifyOtpLoading || phoneOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addDoctors.verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {phoneOtpTimer > 0 ? (
                                            <span data-testid="phone-otp-timer" className="text-xs text-gray-500">{t('addDoctors.resend_in', { seconds: phoneOtpTimer })}</span>
                                        ) : (
                                            <button
                                                data-testid="phone-otp-resend-button"
                                                type="button"
                                                onClick={handleSendPhoneOtp}
                                                disabled={sendOtpLoading}
                                                className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50"
                                            >
                                                {t('addDoctors.resend_otp')}
                                            </button>
                                        )}
                                    </div>
                                    {phoneOtpError && <p data-testid="phone-otp-error" className="text-red-500 text-xs">{phoneOtpError}</p>}
                                </div>
                            )}
                            {phoneOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addDoctors.mobile_otp_verified')}</p>
                            )}
                        </div>

                        <div data-testid="alt-phone-field-container">
                            <label
                                htmlFor="altPhone"
                                data-testid="alt-phone-label"
                                className="block text-sm font-semibold text-gray-700 mb-2"
                            >
                                {t('addDoctors.alt_phone', 'Alternate mobile no')}
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="alt-phone-input"
                                    name="altPhone"
                                    type="text"
                                    value={formData.altPhone}
                                    onChange={handleChange}
                                    placeholder={formData.country === 'India' ? t('addDoctors.phone_placeholder_india') : formData.country === 'France' ? t('addDoctors.phone_placeholder_france') : t('addDoctors.phone_placeholder')}
                                    maxLength={formData.country === 'India' ? 10 : 9}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.altPhone && (
                                <p data-testid="alt-phone-error" className="mt-2 text-xs text-red-500 font-medium">
                                    {errors.altPhone}
                                </p>
                            )}
                        </div>

                        {/* Profession Select */}
                        <div data-testid="profession-field-container">
                            <label data-testid="profession-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('addDoctors.profession', 'Profession')} <span className="text-red-500">*</span>
                            </label>
                            <ProfessionSelect
                                value={formData.profession}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, profession: val }));
                                    if (errors.profession) setErrors(prev => ({ ...prev, profession: '' }));
                                }}
                                professions={professionListAll || []}
                                loading={professionListAllLoading}
                                placeholder={t('addDoctors.select_profession', 'Select Profession')}
                                error={!!errors.profession}
                            />
                            {errors.profession && <p data-testid="profession-error" className="text-red-500 text-xs mt-1">{errors.profession}</p>}
                        </div>

                        <div data-testid="license-number-field-container">
                            <label htmlFor="licenseNumber" data-testid="license-number-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('addDoctors.licensenumber')}
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${licenseStatus === false
                                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                : licenseStatus === true
                                    ? 'border-green-300 focus-within:ring-2 focus-within:ring-green-500'
                                    : errors.licenseNumber
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <FileText className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="license-number-input"
                                    name="licenseNumber"
                                    type="text"
                                    value={formData.licenseNumber}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.license_placeholder')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm pr-8"
                                />
                                {licenseCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2"></span>
                                )}
                                {!licenseCheckLoading && licenseStatus === true && (
                                    <Check className="text-green-500 w-5 h-5 ml-2" />
                                )}
                                {!licenseCheckLoading && licenseStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, licenseNumber: '' }));
                                            setLicenseStatus(null);
                                            setErrors(prev => ({ ...prev, licenseNumber: '' }));
                                            lastCheckedLicenseRef.current = '';
                                        }}
                                    />
                                )}
                            </div>
                            {errors.licenseNumber && <p data-testid="license-number-error" className="mt-2 text-xs text-red-500 font-medium">{errors.licenseNumber}</p>}
                            {charLimitReached.licenseNumber && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: 20 })}</p>}
                        </div>

                        <div data-testid="description-field-container" className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="description" data-testid="description-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.description')}</label>
                            <div className="relative flex items-start border rounded-xl p-3 bg-white border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]">
                                <AlignLeft className="text-[#4ABDBD] w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                                <textarea
                                    data-testid="description-textarea"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.description_placeholder')}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm resize-none"
                                    rows="4"
                                    maxLength={500}
                                />
                            </div>
                            {errors.description && <p data-testid="description-error" className="mt-2 text-xs text-red-500 font-medium">{errors.description}</p>}
                        </div>
                        {/* Country (auto-syncs with phone country code via shared formData.countryId) */}
                        <div data-testid="country-field-container" className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="country" data-testid="country-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.country')} <span className="text-red-500">*</span></label>
                            <CountryTimeZoneSelect
                                countryTimezoneList={countryTimezoneList}
                                countryId={formData.countryId}
                                timeZone={formData.timeZone}
                                onChange={({ countryId, timeZone, countryName }) => {
                                    setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                    setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                    setPhoneStatus(null);
                                    lastCheckedPhoneRef.current = '';
                                }}
                                placeholder={t('addDoctors.selectcountry')}
                                error={!!errors.countryId || !!errors.timeZone}
                            />
                            {errors.countryId && <p data-testid="country-error" className="mt-2 text-xs text-red-500 font-medium">{errors.countryId}</p>}
                            {errors.timeZone && <p className="mt-2 text-xs text-red-500 font-medium">{errors.timeZone}</p>}
                        </div>
                    </form>
                </div>

                {/* Footer Section - Sticky */}
                <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                    <button
                        data-testid="cancel-button"
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                    >
                        {t('addDoctors.cancel')}
                    </button>
                    <button
                        data-testid="submit-button"
                        type="submit"
                        disabled={loading}
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
                    >
                        {loading && (
                            <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                        )}
                        {loading ? t('addDoctors.save') : t('addDoctors.create')}
                    </button>
                </div>
            </div>
        </div>
    );
}